import * as React from 'react';
import {classNames} from '@shopify/react-utilities/styles';
import {focusFirstFocusableNode} from '@shopify/javascript-utilities/focus';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {SocialLink} from 'components/UIKit/Types';
import {Icon, Flex, PoweredByShopify, Text} from 'components/UIKit';
import {NOOP} from 'components/UIKit/utilities';
import {NavItemData} from '../../types';
import {NavDrawerItem} from '../NavDrawerItem';
import {NavSocialLink} from '../NavSocialLink';
import * as locales from '../../locales';
import styles from './NavDrawer.scss';

const DEFAULT_DRAWER_OPTIONS = {
  role: 'navigation',
};

const PERMITTED_NESTED_TYPES = [
  'industry',
  'businessType',
  'location',
  'businessAttribute',
  'salesChannel',
  'curated',
];

interface NavDrawerProps {
  open: boolean;
  role?: string;
  ariaLabel?: string;
  openNavs: string[];
  onOpen: () => any;
  onBack: () => any;
  onCancel: () => any;
  onActiveNavs: (itemPath) => any;
  shopNavItems: NavItemData[];
  infoNavItems: NavItemData[];
  callsToAction: NavItemData[];
  socialLinks: SocialLink[];
  userLinks: NavItemData[];
  user?: boolean;
}

class NavDrawerComponent extends React.Component<NavDrawerProps & WithI18nProps> {
  container: React.RefObject<HTMLDivElement>;
  trigger: React.RefObject<HTMLButtonElement>;

  constructor(props) {
    super(props);
    this.container = React.createRef();
    this.trigger = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (
      !prevProps.open &&
      this.props.open &&
      this.container.current &&
      document.activeElement === this.trigger.current
    ) {
      focusFirstFocusableNode(this.container.current);
    }
  }

  componentDidMount() {
    window.addEventListener('focusin', this.handleFocusin);
  }

  componentWillUnmount() {
    window.removeEventListener('focusin', this.handleFocusin);
  }

  shouldComponentUpdate(nextProps) {
    return (
      nextProps.openNavs.length !== this.props.openNavs.length || nextProps.open !== this.props.open
    );
  }

  findActiveNav = (internalName: string, openNavs: string[] = []) => {
    if (
      openNavs[openNavs.length - 1] === internalName ||
      (openNavs.length === 0 && !internalName)
    ) {
      return true;
    }

    return false;
  };

  restoreFocusToTrigger = () => {
    if (this.trigger.current) {
      this.trigger.current.focus();
    }
  };

  handleFocusin = (event) => {
    const {open, onCancel = NOOP} = this.props;
    if (!open) {
      return;
    }

    if (this.container.current && !this.container.current.contains(event.target)) {
      onCancel();
      this.restoreFocusToTrigger();
    }
  };

  handleContainerKeydown = (event) => {
    if (event.key === 'Escape') {
      const {onCancel = NOOP} = this.props;
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    } else {
      return;
    }

    this.restoreFocusToTrigger();
  };

  handleContainerClick = () => {
    const {onCancel = NOOP} = this.props;
    onCancel();
  };

  handleCloseClick = (event) => {
    event.stopPropagation();

    const {onCancel = NOOP} = this.props;
    onCancel();
    this.restoreFocusToTrigger();
  };

  handleDrawerClick = (event) => event.stopPropagation();

  handleViewClass = (internalName: string, openNavs: string[] = []) => {
    if (this.findActiveNav(internalName, openNavs)) {
      return 'active';
    } else if (openNavs.includes(internalName) || !internalName) {
      return 'wasActive';
    } else {
      return 'inactive';
    }
  };

  handleTabIndex = (open, internalName: string, openNavs: string[] = []) => {
    if (!open) {
      return -1;
    }

    if (this.findActiveNav(internalName, openNavs)) {
      return 0;
    } else if (openNavs.includes(internalName) || !internalName) {
      return -1;
    } else {
      return -1;
    }
  };

  renderNestedList(item, itemPath) {
    if (item === undefined || item.children.length === 0) {
      return null;
    }
    const {onActiveNavs, openNavs, onBack, open, i18n} = this.props;
    const basePath = itemPath.concat(item.internalName);

    const backButtonData = {
      url: '',
      children: [],
      title: i18n.translate('back'),
      pluralizedTitle: i18n.translate('back'),
      internalName: 'back',
    };

    const listItems = item.children.map((child) => {
      const childPath = child.children.length > 0 ? basePath.concat(child.internalName) : basePath;
      return (
        <NavDrawerItem
          key={child.internalName}
          item={child}
          itemPath={childPath}
          onActive={onActiveNavs}
          tabIndex={this.handleTabIndex(open, item.internalName, openNavs)}
          {...item.tracking}
        />
      );
    });

    return (
      <React.Fragment key={item.internalName}>
        <div
          className={classNames(
            styles.view,
            styles[this.handleViewClass(item.internalName, openNavs)],
          )}
        >
          <Text
            text={i18n.translate('drawer.nestedMenu', {menuType: item.title})}
            component="h3"
            hidden
          />
          <NavDrawerItem
            item={backButtonData}
            back
            onBack={onBack}
            tabIndex={this.handleTabIndex(open, item.internalName, openNavs)}
          />
          <ul>{listItems}</ul>
        </div>
        {item.children.map((child) => this.renderNestedList(child, basePath))}
      </React.Fragment>
    );
  }

  render() {
    const {
      open = false,
      role = DEFAULT_DRAWER_OPTIONS.role,
      openNavs,
      onOpen,
      onActiveNavs = NOOP,
      shopNavItems,
      infoNavItems,
      callsToAction,
      socialLinks,
      userLinks,
      user = false,
      i18n,
    } = this.props;

    const tabIndex = this.handleTabIndex(open, '', openNavs);

    return (
      <>
        <button
          ref={this.trigger}
          className={classNames(styles.deviceNav, styles.trigger)}
          onClick={onOpen}
          type="button"
          aria-haspopup
          aria-expanded={open}
          aria-label={i18n.translate('drawer.open')}
        >
          <Icon fill="white" icon="menu" size={20} aria-hidden />
        </button>

        <nav
          className={classNames(styles.overlay, {[styles.open]: open})}
          onClick={this.handleContainerClick}
          onKeyDown={this.handleContainerKeydown}
          role={role}
          ref={this.container}
        >
          <div className={styles.drawer} onClick={this.handleDrawerClick}>
            <div className={styles.header}>
              <button
                type="button"
                className={styles.close}
                onClick={this.handleCloseClick}
                tabIndex={open ? 0 : -1}
                aria-label={i18n.translate('drawer.close')}
              >
                <Icon icon="close" fill="black" size={20} aria-hidden />
              </button>
            </div>

            <div className={styles.body}>
              <div className={classNames(styles.view, styles[this.handleViewClass('', openNavs)])}>
                {user && (
                  <ul className={classNames(styles.section, styles.borderBottom)}>
                    {userLinks.map((item) => (
                      <NavDrawerItem
                        key={item.internalName}
                        item={item}
                        tabIndex={tabIndex}
                        tracking={item.tracking}
                      />
                    ))}
                  </ul>
                )}

                <ul className={classNames(styles.section, styles.borderBottom)}>
                  {callsToAction.map((cta) => (
                    <NavDrawerItem
                      key={cta.internalName}
                      item={cta}
                      tabIndex={tabIndex}
                      tracking={cta.tracking}
                    />
                  ))}
                </ul>

                <ul className={classNames(styles.section, styles.borderBottom)}>
                  {shopNavItems.map((item) => (
                    <NavDrawerItem
                      key={item.internalName}
                      item={item}
                      itemPath={[item.internalName]}
                      onActive={onActiveNavs}
                      tabIndex={tabIndex}
                      tracking={item.tracking}
                    />
                  ))}
                </ul>

                <ul className={classNames(styles.section, styles.borderBottom)}>
                  {infoNavItems.map((item) => (
                    <NavDrawerItem
                      key={item.internalName}
                      item={item}
                      tabIndex={tabIndex}
                      tracking={item.tracking}
                    />
                  ))}
                </ul>

                <div className={classNames(styles.section, styles.padded)}>
                  <Flex wrapperComponent="ul">
                    {socialLinks.map((socialItem) => (
                      <NavSocialLink
                        key={socialItem.name}
                        name={socialItem.name}
                        text={i18n.translate(`social.${socialItem.name}`)}
                        url={socialItem.url}
                        tabIndex={tabIndex}
                      />
                    ))}
                  </Flex>

                  <PoweredByShopify tabIndex={tabIndex} />
                </div>
              </div>

              {PERMITTED_NESTED_TYPES.map((nestedType) =>
                this.renderNestedList(
                  shopNavItems.filter((item) => item.internalName === nestedType)[0],
                  [],
                ))}
            </div>
          </div>
        </nav>
      </>
    );
  }
}

export const NavDrawer = withI18n({
  id: 'NavDrawer',
  fallback: locales.en,
})(NavDrawerComponent);
